import React from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { Tabs } from "@performyard/py-components";

import PageHeading from "../../components/PageHeading";
import DirectoryTab from "./DirectoryTab";

import { useCompanyBasic } from "../../api/hooks";
import { useNavigationConfig } from "../../api/hooks/defs/navigation";

import { hasVisibleTag } from "../../api/services/defs/navigation";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import { OrgChart } from "../OrgChart";

export const DIRECTORY_TAB = "directory";
export const ORG_CHART_TAB = "org-chart";

// InfoPageTemplate adds its standard 24px trailing spacer after mainContent. The
// self-measuring Org Chart must leave room for it or the page gets a dead scroll area.
const ORG_CHART_BOTTOM_OFFSET = 24;

const CompanyDirectory = (): React.ReactElement | null => {
	const [searchParams] = useSearchParams();
	const { data, isLoading } = useNavigationConfig();
	const { company } = useCompanyBasic();

	const config = data?.navigationConfig;

	if (isLoading || !config) {
		return null;
	}

	// Each surface needs its FF visible AND its company function enabled. The server already
	// ANDs both when it builds nav tags (handlers/__init__.py get_company_nav_items), so the
	// tag being present is the whole gate — no second feature-flag lookup here.
	const tabs = [
		...(hasVisibleTag(config, "company", DIRECTORY_TAB)
			? [
					{
						value: DIRECTORY_TAB,
						label: "Directory",
						content: (
							<div className="flex flex-col gap-4">
								<PageHeading>Directory</PageHeading>
								<DirectoryTab />
							</div>
						),
					},
				]
			: []),
		...(hasVisibleTag(config, "company", ORG_CHART_TAB)
			? [
					{
						value: ORG_CHART_TAB,
						label: "Org-Chart",
						content: (
							<div className="flex flex-col gap-4">
								<PageHeading>Org-Chart</PageHeading>
								<OrgChart bottomOffset={ORG_CHART_BOTTOM_OFFSET} />
							</div>
						),
					},
				]
			: []),
	];

	// DirectoryHandler denies this before the page loads; this covers a client-side landing
	// (nav config refetched after a settings change) rather than rendering an empty shell.
	if (tabs.length === 0) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	const tabFromUrl = searchParams.get("tab");
	const activeTab = tabs.some((tab) => tab.value === tabFromUrl) ? (tabFromUrl as string) : tabs[0].value;

	const mainContent = (
		<div data-testid="company-directory">
			{/* A lone tab is just a labelled box around the page, so drop the tab group entirely. */}
			{tabs.length > 1 ? (
				<Tabs
					tabs={tabs}
					value={activeTab}
					urlParam="tab"
					color="neutral"
					size="small"
					dangerouslySetClassName="gap-4"
				/>
			) : (
				tabs[0].content
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			pageTitle={`Directory${company?.name ? ` - ${company.name}` : ""} - PerformYard`}
			mainContent={mainContent}
			showToolbar={false}
		/>
	);
};

export default CompanyDirectory;
